import { getEmptyParagraphNode } from '@doltech/utils/lib/editor';
import { dynamicEditorComponent } from '@doltech/utils/lib/dynamicWithProps/dynamicEditorComponent';
import { isEmpty } from 'lodash';
import { nanoid } from 'nanoid';
import * as React from 'react';
import { Editor, Element, Transforms } from 'slate';
import { ReactEditor, RenderElementProps } from 'slate-react';

import { SlatePlugin } from '../../../../../../slatePlugins/slatePlugins.types';
import { canDeleteNode } from '../../../shared-transforms';
import { ImageSlideShowPluginElementProps } from '../SlideShow/ImageSlideShowPlugin.types';
import {
  ELEMENT_IMAGE_SLIDE_TEXT,
  ELEMENT_IMAGE_SLIDE_TEXT_INPUT,
  ImageSlideTextPluginOptions,
} from './ImageSlideTextPlugin.types';
import { withDynamicBlogEditorPluginHydration } from '@doltech/core/lib/hooks/withDolHydrationOnDemand';
import { ImageSlideTextReadOnlyPlugin } from './ImageSlideTextReadOnlyPlugin';

const ImageSlideShowControlEditElementEditable = withDynamicBlogEditorPluginHydration()(
  dynamicEditorComponent(() =>
    import(/* webpackExports: "ImageSlideShowControlEditElementEditable" */'../SlideShow/components/ImageSlideShowControlEditElement').then(
      (mod) => mod.ImageSlideShowControlEditElementEditable
    )
  )
);
const ImagSlideShowUploaderElement = withDynamicBlogEditorPluginHydration({ canInteract: true })(
  dynamicEditorComponent<ImageSlideShowPluginElementProps>(() =>
    import(/* webpackExports: "ImagSlideShowUploaderElement" */'../SlideShow/components/ImagSlideShowUploaderElement').then(
      (mod) => mod.ImagSlideShowUploaderElement
    )
  )
);
const ImageSlideTextInputElementEditable = withDynamicBlogEditorPluginHydration({
  canInteract: true,
})(
  dynamicEditorComponent(
    () =>
      import(/* webpackExports: "ImageSlideTextInputElementEditable" */'./components/ImageSlideTextInputElement').then(
        (mod) => mod.ImageSlideTextInputElementEditable
      ),
    {
      dependsOn: ImageSlideShowControlEditElementEditable,
    }
  )
);

const matchInputNode = (node) =>
  [ELEMENT_IMAGE_SLIDE_TEXT, ELEMENT_IMAGE_SLIDE_TEXT_INPUT].includes(node?.type as any);

export const ImageSlideTextPlugin = (options?: ImageSlideTextPluginOptions): SlatePlugin => {
  if (options.isReadOnly) {
    return ImageSlideTextReadOnlyPlugin(options);
  }
  return {
    renderElement: (renderProps: RenderElementProps) => {
      const { element } = renderProps;

      if (element?.type === ELEMENT_IMAGE_SLIDE_TEXT) {
        if (isEmpty(element.imageIds)) {
          return <ImagSlideShowUploaderElement title="Slidetext" {...renderProps} {...options} />;
        }

        return <ImageSlideShowControlEditElementEditable {...renderProps} {...options} />;
      }

      if (element?.type === ELEMENT_IMAGE_SLIDE_TEXT_INPUT) {
        return <ImageSlideTextInputElementEditable {...renderProps} {...options} />;
      }
      return undefined;
    },
  };
};

export const withImageSlideTextEditorChain =
  () =>
  <T extends ReactEditor>(editor: T) => {
    const { insertBreak, normalizeNode, deleteBackward, deleteForward } = editor;

    editor.normalizeNode = (entry) => {
      const [node, path] = entry as [any, any[]];

      switch (node?.type) {
        case ELEMENT_IMAGE_SLIDE_TEXT: {
          if (node.imageIds?.length > 0) {
            // imageIds must have ELEMENT_IMAGE_SLIDE_TEXT_INPUT as a child
            const imageIdsWithoutInputNode = node.imageIds.filter(
              (imageId) => !node.children.find((childNode) => childNode.imageId === imageId)
            );
            if (imageIdsWithoutInputNode.length > 0) {
              const imageId = imageIdsWithoutInputNode[0];
              Transforms.insertNodes(
                editor,
                {
                  id: nanoid(),
                  type: ELEMENT_IMAGE_SLIDE_TEXT_INPUT,
                  imageId,
                  children: [getEmptyParagraphNode()],
                },
                { at: [...path, node.children.length - 1] }
              );

              return;
            }
          }

          // ELEMENT_IMAGE_SLIDE_TEXT can't contain ELEMENT_IMAGE_SLIDE_TEXT
          const foundChildrenIndex = node.children.findIndex(
            (n) => n?.type === ELEMENT_IMAGE_SLIDE_TEXT
          );
          if (foundChildrenIndex > -1) {
            Transforms.liftNodes(editor, { at: [...path, foundChildrenIndex] });
            return;
          }

          // every children type is always ELEMENT_IMAGE_SLIDE_TEXT_INPUT
          const foundInvalidChildrenIndex = (node.children as any[]).lastIndexOf(
            (n) => n?.type !== ELEMENT_IMAGE_SLIDE_TEXT_INPUT
          );
          if (foundInvalidChildrenIndex > -1) {
            Transforms.removeNodes(editor, { at: [...path, foundChildrenIndex] });
            return;
          }
          break;
        }
        case ELEMENT_IMAGE_SLIDE_TEXT_INPUT: {
          if (!node.imageId) {
            Transforms.removeNodes(editor, { at: path });
            return;
          }
          // this input always have imageId, parent of this node always got imageIds which have this imageIdd
          let parentNode = null;
          try {
            [parentNode] = Editor.above(editor, { at: path });
            // eslint-disable-next-line no-empty
          } catch (ex) {}
          if (parentNode?.type === ELEMENT_IMAGE_SLIDE_TEXT) {
            if (!parentNode.imageIds.includes(node.imageId)) {
              Transforms.removeNodes(editor, { at: path });
              return;
            }
          } else {
            Transforms.removeNodes(editor, { at: path });
            return;
          }

          break;
        }
      }

      normalizeNode(entry);
    };

    editor.deleteBackward = (unit) => {
      const canDelete = canDeleteNode({
        editor,
        unit,
        direction: 'backward',
        match: matchInputNode,
      });
      if (!canDelete) {
        return;
      }
      deleteBackward(unit);
    };

    editor.deleteForward = (unit) => {
      const canDelete = canDeleteNode({
        editor,
        unit,
        direction: 'forward',
        match: matchInputNode,
      });
      if (!canDelete) {
        return;
      }
      deleteForward(unit);
    };

    editor.insertBreak = () => {
      const { selection } = editor;

      if (selection) {
        const [table] = Editor.nodes(editor, {
          match: (n: any) =>
            !Editor.isEditor(n) &&
            Element.isElement(n) &&
            n.type === ELEMENT_IMAGE_SLIDE_TEXT_INPUT,
        });

        if (table) {
          editor.insertText('\n');
          return;
        }
      }

      insertBreak();
    };

    return editor;
  };
